// PR-7.2 — pure helpers for the reschedule_audit_log INSERTs. Kept in
// lib/ so vitest can import without the supabase client. Each
// state-change site (RescheduleRequest, RescheduleCard,
// AdminRescheduleQueue) calls buildAuditLogEntry() to produce the
// canonical payload, then INSERTs via supabase.
//
// Centralising the payload shape:
//   - Guards against drift across the 5 sites
//   - Documents each action's `metadata` shape
//   - RLS-safe — actor_id MUST equal auth.uid() per the WITH CHECK
//     policy from the migration; the helper just shapes, the caller
//     supplies actor_id from useAuth().

export type RescheduleAuditAction =
  | "request_created"
  | "opponent_accepted"
  | "opponent_rejected"
  | "counter_proposed"
  | "admin_approved"
  | "admin_denied";

export type RescheduleAuditActorRole = "player" | "admin";
// 'system' role is reserved for SECURITY DEFINER server-side cron
// inserts (PR-7.3). Direct client INSERTs are blocked by the RLS
// WITH CHECK clause on the migration.

export interface RescheduleAuditEntry {
  request_id: string;
  parent_request_id: string | null;
  action: RescheduleAuditAction;
  actor_id: string;
  actor_role: RescheduleAuditActorRole;
  metadata: Record<string, unknown>;
}

/**
 * Builds the canonical audit-log INSERT payload for a state-change
 * action. `metadata` includes optional shape per action — see comment
 * on each callsite below.
 */
export function buildAuditLogEntry(args: {
  action: RescheduleAuditAction;
  requestId: string;
  actorId: string;
  actorRole: RescheduleAuditActorRole;
  parentRequestId?: string | null;
  metadata?: Record<string, unknown>;
}): RescheduleAuditEntry {
  return {
    request_id: args.requestId,
    parent_request_id: args.parentRequestId ?? null,
    action: args.action,
    actor_id: args.actorId,
    actor_role: args.actorRole,
    metadata: args.metadata ?? {},
  };
}

/**
 * Convenience: derives a human-readable Swedish label for the action.
 * Used by the (future, PR-7.2-followup) admin audit-log viewer.
 * Kept here so the action enum has a single source of truth.
 */
export function actionLabelSv(action: RescheduleAuditAction): string {
  switch (action) {
    case "request_created":
      return "Förslag skickat";
    case "opponent_accepted":
      return "Motståndare accepterade";
    case "opponent_rejected":
      return "Motståndare nekade";
    case "counter_proposed":
      return "Motförslag skickat";
    case "admin_approved":
      return "Admin godkände";
    case "admin_denied":
      return "Admin nekade";
  }
}
